/* eslint-disable */
import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  TextField,
  CircularProgress,
} from '@mui/material'
import { notifyError, notifySuccess } from '../../../utils/notify'
import { uploadFileToDocumento } from '../../../utils/oikosUploadDocumento'
import { desvincularProjFat, getProjIdFromRec, invalidateFatSupportCache, loadFatSupportStatus } from '../../../utils/fatSupportDocs'
import { enviarEmailNfseTeste } from '../../../utils/kaminoNfse'
import { ECOSSIS_PJ_IDS, TP_DOC_OUTROS } from '../../../constants/oikosReceitas'

const fmt = (d) => {
  if (!d) return ''
  const s = String(d).slice(0, 10)
  const [y, m, day] = s.split('-')
  if (!y || !m || !day) return s
  return `${day}/${m}/${y}`
}

const statusChip = (row) => {
  if (row.status === 'ok') {
    return <Chip size="small" color="success" label={row.doc?.documento_fimVig ? `Válido até ${fmt(row.doc.documento_fimVig)}` : 'Vigente'} />
  }
  if (row.status === 'expired') {
    return <Chip size="small" color="warning" label={row.doc?.documento_fimVig ? `Vencido em ${fmt(row.doc.documento_fimVig)}` : 'Sem validade'} />
  }
  return <Chip size="small" color="error" variant="outlined" label="Sem arquivo" />
}

const emptyDraft = () => ({ file: null, dtEmissao: '', fimVig: '' })

export default function ModalDocsSuporteEnvio({ open, rec, onClose }) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [rows, setRows] = useState([])
  const [drafts, setDrafts] = useState({})

  const recId = Number(rec?.rec_id) || 0
  const pjPrestador = Number(rec?.pj_prestador) || 0
  const [projId, setProjId] = useState(0)

  const setDraft = (key, patch) => {
    setDrafts((cur) => ({ ...cur, [key]: { ...(cur[key] || emptyDraft()), ...patch } }))
  }

  useEffect(() => {
    if (!open) return undefined
    let cancel = false
    const load = async () => {
      setLoading(true)
      try {
        const pid = await getProjIdFromRec(rec)
        if (cancel) return
        setProjId(pid)
        const list = pid > 0 ? await loadFatSupportStatus({ projId: pid }) : []
        if (cancel) return
        setRows(list)
        const next = {}
        list.forEach((r) => { next[r.key] = emptyDraft() })
        setDrafts(next)
        if (!(pid > 0)) {
          notifyError('Não foi possível identificar o projeto desta receita.')
        }
      } catch (e) {
        console.log('ModalDocsSuporteEnvio load', e)
        notifyError('Não foi possível verificar os documentos do processo de faturamento.')
      } finally {
        if (!cancel) setLoading(false)
      }
    }
    load()
    return () => { cancel = true }
  }, [open, rec])

  const pending = rows.filter((r) => r.status !== 'ok')

  const submit = async () => {
    if (!recId) return
    for (const row of pending) {
      const d = drafts[row.key] || emptyDraft()
      if (!d.file) {
        notifyError(`Anexe o arquivo de ${row.label}.`)
        return
      }
      if (!d.fimVig) {
        notifyError(`Informe a validade de ${row.label}.`)
        return
      }
    }
    setSaving(true)
    try {
      const pjIds = [...new Set([pjPrestador, ...ECOSSIS_PJ_IDS].filter((id) => id > 0))]
      for (const row of pending) {
        const d = drafts[row.key]
        const extraVincs = [
          ...pjIds.map((id) => ({ tabRef: 'pj', tabId: id })),
          projId ? { tabRef: 'proj', tabId: projId } : null,
        ].filter(Boolean)
        await uploadFileToDocumento({
          tpDocumentoId: row.tpDocumento_id || TP_DOC_OUTROS,
          tabRef: 'projFat',
          tabId: projId,
          file: d.file,
          documentoDesc: `${row.label}${d.fimVig ? ` (válido até ${fmt(d.fimVig)})` : ''}`,
          documentoDtEmissao: d.dtEmissao || undefined,
          documentoIniVig: d.dtEmissao || undefined,
          documentoFimVig: d.fimVig,
          extraVincs,
        })
        if (row.documentoVinc_id) await desvincularProjFat(row.documentoVinc_id)
      }
      await enviarEmailNfseTeste({ rec_id: recId })
      invalidateFatSupportCache(rec?.orcRubr_id)
      notifySuccess('E-mail de teste enviado para bernardo.alcalde@ecossis.com.br.')
      onClose()
    } catch (err) {
      console.log('ModalDocsSuporteEnvio submit', err?.response?.data || err)
      notifyError(err?.message || 'Não foi possível concluir o envio.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>Documentação de suporte da NFS-e</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography variant="body2">Verificando os documentos selecionados no processo de faturamento…</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Estes são os arquivos marcados na aba Eventos de pagamento (processo de faturamento). Se faltar o arquivo ou a validade tiver vencido, anexe a versão vigente — ela entra na tabela documento e substitui o vínculo deste faturamento.
            </Typography>
            {rows.length === 0 && (
              <Typography variant="body2">
                Nenhum documento de suporte selecionado neste projeto. O e-mail seguirá só com o XML e o PDF da nota. Para incluir CNDs, guias ou folha, marque-os no processo de faturamento da aba Eventos de pagamento.
              </Typography>
            )}
            {rows.map((row) => {
              const d = drafts[row.key] || emptyDraft()
              const need = row.status !== 'ok'
              return (
                <Box
                  key={row.key}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: need ? 1 : 0, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ flex: 1 }}>{row.label}</Typography>
                    {statusChip(row)}
                  </Box>
                  {need && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button size="small" variant="outlined" component="label" disabled={saving}>
                        {d.file ? d.file.name : 'Escolher arquivo'}
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.png,.jpg,.jpeg,.xml,.zip"
                          onChange={(e) => {
                            const file = e.target.files && e.target.files[0]
                            e.target.value = ''
                            setDraft(row.key, { file: file || null })
                          }}
                        />
                      </Button>
                      <TextField
                        size="small"
                        type="date"
                        label="Emissão"
                        InputLabelProps={{ shrink: true }}
                        value={d.dtEmissao}
                        onChange={(e) => setDraft(row.key, { dtEmissao: e.target.value })}
                        sx={{ width: 170 }}
                      />
                      <TextField
                        size="small"
                        type="date"
                        label="Válido até"
                        InputLabelProps={{ shrink: true }}
                        value={d.fimVig}
                        onChange={(e) => setDraft(row.key, { fimVig: e.target.value })}
                        required
                        sx={{ width: 170 }}
                      />
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={loading || saving || !recId || !projId}>
          {saving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          {pending.length ? 'Anexar e enviar e-mail' : 'Enviar e-mail'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
